import React, {
	useContext,
	useState,
	useCallback,
	useMemo,
	useEffect,
} from "react";
import Button from "../UI/Button";
import { useNavigate } from "react-router-dom";
import { Flex } from "reflexbox/styled-components";
import { Body } from "../UI/Text";
import Link from "../UI/Link";
import { DropdownWrapper, HeaderWrapper, DropdownItems } from "./styles";
import { useTheme } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { updateUser } from "../../requests/userManagementRequests";
import * as fa from "@fortawesome/free-solid-svg-icons";
import UserContext from "../../context/user/UserContext";
import * as constants from "../../modules/constants";
import SocketContext from "../../context/socket/SocketContext";
import { useQuery } from "../Hooks";
import { getGroups } from "../../requests/groupManagementRequests";
import _ from "lodash";
import Loading from "../UI/Loading";

const Header = ({ refetchTheme }) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const {
		activeUser,
		darkMode,
		setDarkMode,
		username,
		setActiveUser,
		loggedIn,
		setLoggedIn,
		platform,
		pid,
		refetchHeader,
		setRefetchHeader,
		isLoading: userLoading,
	} = useContext(UserContext);
	const { client } = useContext(SocketContext);

	const {
		data: groupData,
		refetchQuery,
		isLoading,
	} = useQuery({
		queryID: "headergroups",
		queryVariables: { pid, username },
		forcedFilters: {},
		skip: !loggedIn,
		request: getGroups,
	});

	useEffect(() => {
		if (refetchHeader && !isLoading) {
			refetchQuery();
			setRefetchHeader(false);
		}
	}, [isLoading, refetchHeader, refetchQuery, setRefetchHeader]);

	const { rows: groups = [] } = groupData || {};

	const { groupId } = activeUser || {};

	const groupOptions = useMemo(() => {
		if (!groupData?.rows) return null;
		const newGroups = [...groupData?.rows];
		_.remove(newGroups, ({ id: gid }) => gid === groupId);
		return newGroups;
	}, [groupData, groupId]);

	const { name } = platform || {};

	const [toggleOpen, setToggleOpen] = useState(false);
	const [leftToggleOpen, setLeftToggleOpen] = useState(false);

	const darkModeToggle = useCallback(() => {
		window.sessionStorage.setItem("darkMode", !darkMode);
		setDarkMode(!darkMode);
		refetchTheme();
	}, [darkMode, refetchTheme, setDarkMode]);

	const signOut = useCallback(async () => {
		const { username, id } = activeUser;
		if (client)
			client.send(
				JSON.stringify({
					type: "userevent",
					username,
					content: { isLoggedIn: false },
				})
			);

		await updateUser({ id, input: { isLoggedIn: false } });
		window.sessionStorage.removeItem("token");
		window.localStorage.removeItem("token");
		setActiveUser({});
		setToggleOpen(false);
		setLeftToggleOpen(false);
		setLoggedIn(false);
		navigate("/Login");
		refetchQuery();
	}, [activeUser, client, setActiveUser, setLoggedIn, navigate, refetchQuery]);

	const currentGroup = useMemo(() => {
		const currentGroup = _.find(groups, ({ id: gid }) => gid === groupId);

		return currentGroup;
	}, [groupId, groups]);

	const { allowRMAs } = platform || {};

	// const { data: interactions } = useQuery({
	//   request: getInteractions,
	//   queryVariables: {
	//     groupId,
	//     firstName: activeUser?.firstName,
	//     lastName: activeUser?.lastName,
	//   },
	//   skip: userLoading || isLoading,
	// });
	// const { data: interactionTopics } = useQuery({
	//   request: getInteractionTopics,
	//   queryVariables: { groupId },
	//   forcedFilters: {},
	//   skip: userLoading || isLoading,
	// });
	// console.log({ interactions, interactionTopics });

	const assignUserGroup = useCallback(
		async (group) => {
			navigate("/QuoteDashboard");
			const { id: gid } = group || {};
			const { id } = activeUser || {};
			setActiveUser({ ...activeUser, groupId: gid });
			await updateUser({ id, input: { groupId: gid } });
		},
		[activeUser, navigate, setActiveUser]
	);

	return (
		<HeaderWrapper className="no-print" darkMode={darkMode}>
			<Flex alignItems="center">
				<Link
					to={
						!activeUser
							? "/"
							: constants.DEFAULT_HOMEPAGE[activeUser?.roleLevel || 0]
					}
				>
					<img
						style={{
							maxWidth: "10rem",
							minWidth: "2rem",
							width: "100%",
							height: "auto",
							minHeight: "2rem",
							maxHeight: "3rem",
							marginLeft: "1rem",
						}}
						src={theme.logo}
						alt={name || "SwiftSell"}
					/>
				</Link>
				{isLoading ? (
					<DropdownWrapper darkMode={darkMode}>
						<Loading inline />
					</DropdownWrapper>
				) : (
					<>
						{groups?.length > 0 && activeUser?.roleLevel !== 0 && (
							<DropdownWrapper
								onMouseLeave={() => setLeftToggleOpen(false)}
								toggleOpen={leftToggleOpen}
								darkMode={darkMode}
							>
								<>
									<Button
										className="header-dropdown header-link"
										iconPosition="left"
										iconName="faUserGroup"
										textOnly
										onClick={() => setLeftToggleOpen(!leftToggleOpen)}
									>
										<Body ellipsis className="username">
											{currentGroup?.name}
										</Body>
										{groups?.length > 1 && (
											<FontAwesomeIcon
												icon={leftToggleOpen ? fa.faAngleUp : fa.faAngleDown}
											/>
										)}
									</Button>
								</>

								{groups.length > 1 && activeUser?.roleLevel !== 0 && (
									<DropdownItems className="open group">
										{groupOptions.map((group) => {
											return (
												<Button
													key={group?.id}
													className="header-link"
													textOnly
													onClick={() => assignUserGroup(group)}
													iconName="faUserGroup"
													iconPosition="left"
												>
													<Body ellipsis className="username">
														{group?.name}
													</Body>
												</Button>
											);
										})}
									</DropdownItems>
								)}
							</DropdownWrapper>
						)}
					</>
				)}
			</Flex>

			<Flex alignItems="center">
				{!activeUser || activeUser?.roleLevel === 0 ? null : (
					<>
						<Button
							className="header-link hideable-header"
							iconPosition="left"
							iconName="faFileContract"
							textOnly
							to="/QuoteDashboard"
						>
							Quotes
						</Button>
						<Button
							className="header-link hideable-header"
							iconPosition="left"
							iconName="faTruckFast"
							textOnly
							to={`/MyOrders/${activeUser?.username}`}
						>
							Orders
						</Button>

						{/* only show the RMA button to groups that submit parts orders */}

						{allowRMAs && currentGroup?.productList === 1001 && (
							<Button
								className="header-link hideable-header"
								iconPosition="left"
								iconName="faTags"
								textOnly
								to={`/RMAs/${pid}`}
							>
								RMAs
							</Button>
						)}
					</>
				)}

				<DropdownWrapper
					onMouseLeave={() => setToggleOpen(false)}
					toggleOpen={toggleOpen}
					darkMode={darkMode}
				>
					{!loggedIn ? (
						<Button
							className="header-dropdown header-link"
							iconPosition="left"
							iconName="faUser"
							textOnly
							to="/Login"
						>
							Login
						</Button>
					) : (
						<>
							<Button
								className="header-dropdown header-link"
								iconPosition="left"
								iconName="faUser"
								textOnly
								onClick={() => setToggleOpen(!toggleOpen)}
							>
								<Body ellipsis className="username">
									{username || activeUser?.username}
								</Body>
								<FontAwesomeIcon
									icon={toggleOpen ? fa.faAngleUp : fa.faAngleDown}
								/>
							</Button>
						</>
					)}

					<DropdownItems className="open">
						{!activeUser || activeUser?.roleLevel === 0 ? null : (
							<>
								<Button
									className="header-link hideable-dropdown"
									iconPosition="left"
									iconName="faBorderAll"
									textOnly
									to="/QuoteDashboard"
								>
									Quotes
								</Button>
								<Button
									className="header-link hideable-dropdown"
									iconPosition="left"
									iconName="faTags"
									textOnly
									to={`/MyOrders/${activeUser?.username}`}
								>
									Orders
								</Button>
							</>
						)}
						{activeUser?.roleLevel >= 5 && (
							<Button
								className="header-link"
								textOnly
								to="/Administration"
								iconName="faUserGear"
								iconPosition="left"
							>
								Administration
							</Button>
						)}

						<Button
							className="header-link"
							textOnly
							to="/Catalog"
							iconName="faBook"
							iconPosition="left"
						>
							Product Catalog
						</Button>

						<Button
							className="header-link"
							textOnly
							iconPosition="left"
							iconName="faUserPen"
							to={`/MyProfile/${activeUser?.username}`}
						>
							My Profile
						</Button>
						{/* <Button
              className="header-link"
              textOnly
              iconPosition="left"
              iconName="faComments"
              to={`/Interactions/${activeUser?.username}`}
            >
              My Interactions
            </Button> */}
						{/* <Button
              className="header-link"
              textOnly
              to="/MyConfigurations"
              iconPosition="left"
              iconName="faPenRuler"
            >
              My Configurations
            </Button> */}
						<Button
							iconPosition="left"
							className="header-link"
							textOnly
							iconName="faArrowRightFromBracket"
							onClick={signOut}
						>
							Sign Out
						</Button>
						<Button className="header-link" textOnly onClick={darkModeToggle}>
							<FontAwesomeIcon icon={darkMode ? fa.faMoon : fa.faSun} />
							<Body>Dark Mode</Body>
							<FontAwesomeIcon
								icon={darkMode ? fa.faToggleOn : fa.faToggleOff}
								size="lg"
							/>
						</Button>
					</DropdownItems>
				</DropdownWrapper>
			</Flex>
		</HeaderWrapper>
	);
};

export default Header;
